/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useRef, useState, ReactElement } from "react";

import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import {
  Dropdown,
  IconButton,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import {
  errorText,
  fileInputs,
  fileUploadContainer,
  iconButton,
  summaryText,
} from "Components/ManageCodesList/ManageCodesList.styles";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { CODE_UPLOADS_TEMPLATE } from "Constants/ManageCodesConstants";
import {
  getBenefitNamesOptionList,
  getSubmitCsvFileResponse,
  getUploadCsvResponse,
  setSubmitCsvFileResponse,
  setUploadCsvResponse,
} from "Dataflows/Benefit/ManageCodesSlice";
import { ISubmitCSVFileRequest } from "Interfaces/Benefits/ISubmitCSVFileRequest";
import { IBenefitUploadCSVFileRequest } from "Interfaces/Benefits/IUploadCSVFileRequest";
import { IUploadCsvFileValidation } from "Interfaces/SA/IUploadCSVFileRequest";
import { useSubmitCsvFileMutation, useUploadCsvFileMutation } from "Services/ManageCodesApi";
import { constructCsvDownload } from "Utility/DownloadHelper";
import { convertCsvToBase64, validateBenefitsFileUpload } from "Utility/FileUploadHelper";

import { IManageCodesSidePanelProps } from "./IManageCodesSidePanelProps";

/**
 * Manage Codes Side Panel component.
 * @param {IManageCodesSidePanelProps} props the component props.
 * @returns {ReactElement} the component element.
 */
export const ManageCodesSidePanel = (props: IManageCodesSidePanelProps): ReactElement => {
  const dispatch = useDispatch();

  const [uploadCsvFile, { isLoading: isUploading }] = useUploadCsvFileMutation();

  const [submitCsvFile, { isLoading: isSubmitting }] = useSubmitCsvFileMutation();

  const { isPanelOpen, setIsPanelOpen } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileUploadError, setFileUploadError] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [benefitName, setBenefitName] = useState("Any");
  const uploadCsvResponse = useSelector(getUploadCsvResponse);
  const submitCsvFileResponse = useSelector(getSubmitCsvFileResponse);
  const benefitNamesOptionList = useSelector(getBenefitNamesOptionList);

  /**
   * dismiss Panel function.
   */
  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    setFileUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setBenefitName("Any");
    setSelectedFileName("");
    dispatch(setUploadCsvResponse({} as IUploadCsvFileValidation));
    dispatch(setSubmitCsvFileResponse({} as any));
  };

  useEffect(() => {
    if (submitCsvFileResponse.isSuccess) {
      dismissPanel();
      setBenefitName("Any");
      setSelectedFileName("");
    }
  }, [submitCsvFileResponse]);

  /**
   * Reset form state when panel opens
   */
  useEffect(() => {
    if (isPanelOpen) {
      setFileUploadError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isPanelOpen, dispatch]);

  /**
   * Handle submit click.
   */
  const handleSubmit = () => {
    const request: ISubmitCSVFileRequest = {
      fileId: uploadCsvResponse.fileId,
      benefitName: benefitName,
    };
    const tenant = props.tenant;
    submitCsvFile({ tenant, request })
      .unwrap()
      .then((response) => {
        if (!response.isSuccess) {
          setFileUploadError("Submission failed: " + response.errors?.join(", "));
        }
        dispatch(setSubmitCsvFileResponse(response));
      })
      .catch((error) => {
        setFileUploadError("Error submitting file: " + error.message);
      });
  };

  /**
   * handle Download Static CSV.
   */
  const handleDownloadStaticCSV = (): void => {
    constructCsvDownload(CODE_UPLOADS_TEMPLATE, "Code_Uploads_Template.csv");
  };

  /**
   * handle File upload Change.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (benefitName === "Any") {
      setFileUploadError("Please select a benefit name");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      setFileUploadError("");
      const errorMessage = validateBenefitsFileUpload(event);
      if (errorMessage) {
        setFileUploadError(errorMessage);
      } else {
        const file = event.target.files;
        if (file && file.length > 0) {
          setSelectedFileName(file[0].name);
          const tenant = props.tenant;
          convertCsvToBase64(file[0])
            .then((base64String) => {
              const request: IBenefitUploadCSVFileRequest = {
                CsvBase64: base64String,
                BenefitName: benefitName,
              };

              uploadCsvFile({ tenant, request })
                .unwrap()
                .then((response) => {
                  setFileUploadError("");
                  dispatch(setUploadCsvResponse(response));
                })
                .catch((error) => {
                  setFileUploadError("Error uploading file");
                });
            })
            .catch((error) => {
              setFileUploadError("Error converting file");
            });
        }
      }
    }
  };

  return (
    <form id="sdf">
      <SidePanel
        isOpen={isPanelOpen}
        title="Add/modify Codes"
        saveButtonLabel="Submit"
        disableActions={!uploadCsvResponse.isValid}
        onDismiss={dismissPanel}
        onSave={() => handleSubmit()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem align="start">
            <Text>
              Follow the steps below to add new codes or modify existing codes. Please note{" "}
              <strong>this action is irreversible.</strong>
            </Text>
          </StackItem>
          <StackItem>
            <Text>1. Select a benefit</Text>
            <StackItem>
              <Dropdown
                options={benefitNamesOptionList}
                onChange={(_e, op): void => {
                  setBenefitName(op?.key as string);
                }}
                selectedKey={benefitName}
                title="Benefit Dropdown"
                styles={{ dropdown: { maxWidth: 300 } }}
              />
            </StackItem>
          </StackItem>
          <StackItem>
            <Text>2. Download CSV template (optional)</Text>
            <StackItem>
              <Button onClick={handleDownloadStaticCSV}>Download</Button>
            </StackItem>
          </StackItem>
          <StackItem>
            <Text>3. Edit the CSV file</Text>
            <br />
            <Text>To map a code to a user, enter their user ID in the User ID column.</Text>
            <br />
            <Text>To unmap a code a user, do not include a user ID in the Assigned-to column.</Text>
            <br />
            <Text>
              To expire a code and make it unavailable, add a past date in the Expiration Date
              column.
            </Text>
          </StackItem>
          <StackItem>
            <Text>4. Upload your CSV file</Text>
            <br />
            <div style={fileUploadContainer}>
              <input
                type="text"
                placeholder="Select a file"
                style={fileInputs}
                value={selectedFileName}
                readOnly
              />
              <IconButton
                disabled={isUploading}
                styles={iconButton}
                iconProps={{ iconName: "FabricFolder" }}
                onClick={(): void => fileInputRef.current?.click()}
                tabIndex={0}
              ></IconButton>
              <input
                hidden
                id="base64str"
                name="base64str"
                type="file"
                accept=".csv"
                ref={fileInputRef}
                multiple={false}
                onChange={handleFileChange}
              />
              {isUploading && <Spinner size={SpinnerSize.small} />}
            </div>
            <div style={errorText}>{fileUploadError}</div>
          </StackItem>
          {uploadCsvResponse?.summary?.length > 0 && (
            <StackItem style={summaryText}>
              {uploadCsvResponse.summary.map((item: string, index) => {
                return <p key={"summary" + index}>{item}</p>;
              })}
            </StackItem>
          )}
          {uploadCsvResponse?.errors?.length > 0 && (
            <StackItem style={errorText}>
              {uploadCsvResponse.errors.map((item: string, index) => {
                return <p key={"errors" + index}>{item}</p>;
              })}
            </StackItem>
          )}
          {isSubmitting && <Spinner size={SpinnerSize.small} />}
        </Stack>
      </SidePanel>
    </form>
  );
};
